/**
 * BrainEnginePanel — Admin panel for AI engine configuration
 * Single source of truth for all AI parameters (zero-code tuning)
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../../common/contexts/ProjectContext";
import { authFetch } from "../../../services/authFetch";
import { ApiKeyManager } from "../components/ApiKeyManager";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import helpMarkdownText from "../../../../docs/brain_engine_help.md?raw";
import { CustomDialog } from "../../common/components/CustomDialog";

// ─── Types ───
interface Preset {
  id: string;
  name: string;
  is_active: boolean;
  is_default: boolean;
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

interface TooltipDef {
  label: string;
  tooltip: string;
  type: "slider" | "number" | "textarea" | "select" | "json";
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  dynamicOptions?: string;
  prependOptions?: string[];
  category?: string;
}

// ─── Friendly tooltips with analogies ───
const FRIENDLY_TOOLTIPS: Record<string, string> = {
  "llm.generatorTemperature":
    "Es como el volante de un coche: a 0 va recto sin desviarse (respuestas predecibles), a 0.3 conduce natural, a 1.0 da bandazos (creativo pero impredecible). Para datos de producción, quédate entre 0.1 y 0.5.",
  "llm.filterTemperature":
    'Controla cuánto "improvisa" la IA al decidir qué historias incluir. Más bajo = más estricto, más alto = más permisivo. Si ves que excluye stories relevantes, sube un poco.',
  "llm.filterMaxTokens":
    'Es la "extensión máxima" de la respuesta del Brain Selector. Como dar un límite de palabras en un examen — si es muy bajo, la IA puede cortar antes de terminar.',
  "llm.filterModel":
    "Es como tener dos equipos: uno rápido y barato que descarta los expedientes irrelevantes (Brain Selector), y otro experto y caro que escribe la respuesta final (generador). Puedes poner el modelo más ligero aquí para ahorrar coste sin perder calidad.",

  "thresholds.hybridTokenThreshold":
    "Divide modo RAW de HYBRID. Si el contenido estimado es menor que este número de tokens, carga todo al detalle (RAW). Si es mayor, mezcla resúmenes + fragmentos selectos (HYBRID). Más alto = más queries en modo RAW (mejor calidad).",
  "thresholds.summaryTokenThreshold":
    "Si el contenido estimado supera este número de tokens, la IA usa SOLO resúmenes de las historias en vez de datos completos. Más alto = tarda más pero ve más detalle antes de pasar a solo resúmenes.",

  "sampling.segsPerStoryBase":
    'Es la "parcela total" de fragmentos a repartir entre todas las historias. Si tienes 100 historias y este valor es 8000, cada historia recibe ~80 fragmentos. Más alto = más detalle pero más lento y caro.',
  "sampling.segsPerStoryMin":
    "El mínimo de fragmentos que recibe cada historia, sin importar cuántas haya. Es como garantizar al menos una foto de cada participante en un álbum, aunque haya muchos.",
  "sampling.otfSegmentCap":
    "Las entrevistas (OTFs) son la joya de la corona — lo que dicen a cámara. Este número controla cuántos fragmentos de cada entrevista se incluyen. Más alto = respuestas con más testimonios reales.",
  "sampling.orphanCap":
    'Fragmentos "huérfanos" que no pertenecen a ninguna historia específica. Como piezas sueltas de un puzzle: incluir más puede revelar conexiones inesperadas, pero demasiados añaden ruido.',
  "sampling.coreRelevanceThreshold":
    'Cuántas veces debe mencionarse un participante en una story para considerarla "principal". Las principales reciben datos completos, el resto solo resúmenes.',
  "sampling.segsPerCore":
    'Fragmentos a cargar por cada historia "principal" en modo HYBRID. Más alto = más detalle de las historias clave donde aparece el participante.',
  "sampling.maxSegmentChars":
    "Longitud máxima de cada fragmento de transcripción. Fragmentos muy largos se truncan. Más alto = más contexto por fragmento pero contexto más pesado.",

  "estimation.avgTokensPerLog":
    "Cuántos tokens estima el sistema por cada entrada de log. Se usa para calcular el peso del contexto antes de cargarlo.",
  "estimation.avgTokensPerSegment":
    "Cuántos tokens estima el sistema por cada fragmento de transcripción. Los fragmentos suelen ser más largos que los logs.",

  "prompts.maxContextTokens":
    'Tamaño máximo del contexto en tokens. Piensa en ello como el tamaño del "maletín" que le das a la IA: más grande = más documentos puede consultar, pero Gemini tiene un límite de ~1M tokens.',
  "limits.rateLimitPerMin":
    "Máximo de preguntas por minuto por usuario. Controla el flujo para proteger el sistema de un uso excesivo.",
  "limits.dailyBudgetPerUser":
    "Presupuesto diario máximo en dólares por usuario. Cuando se agota, el usuario no puede hacer más preguntas hasta el día siguiente.",

  "brainSelector.enabled":
    "El Brain Selector pre-filtra las stories relevantes usando un catálogo jerárquico con metadata estructurada (participantes, tipo, tags, keywords). Es como darle un índice del libro para que seleccione qué capítulos leer.",
  "brainSelector.selectorTokenThreshold":
    "Si el contenido estimado supera estos tokens, se activa el Brain Selector para pre-filtrar. Si es menor, usa todo directamente.",
  "brainSelector.minStories":
    "Si el Brain Selector devuelve muy pocas historias (menos de este número), descarta el resultado y usa todas. Protección contra filtrado excesivo.",
  "brainSelector.catalogPrompt":
    "Es la instrucción que recibe la IA junto con el catálogo jerárquico. Le dice cómo usar la metadata para seleccionar las historias más relevantes a la pregunta.",

  pricing:
    "Tabla de precios por modelo ($/1M tokens). Se usa para estimar el coste de cada query. Actualizar cuando Google cambie precios o añadas nuevos modelos.",
};

// Short hints visible under each control
const PARAM_HINTS: Record<string, string> = {
  "estimation.avgTokensPerLog": "⬆ más = estima contexto más pesado",
  "estimation.avgTokensPerSegment": "⬆ más = estima más peso por fragmento de audio",
  "brainSelector.enabled": "Pre-filtra stories antes de cargar datos",
  "brainSelector.selectorTokenThreshold": "⬆ más = se activa con contextos más grandes",
  "brainSelector.minStories": "⬆ más = rechaza filtrados muy agresivos",
  "brainSelector.catalogPrompt": "Instrucciones para el filtro de stories",
  "llm.filterModel": "Modelo barato para pre-filtrar stories",
  "llm.filterTemperature": "⬆ más = más permisivo al filtrar",
  "llm.filterMaxTokens": "⬆ más = respuestas de filtrado más largas",
  "thresholds.hybridTokenThreshold": "⬆ más = más queries usan datos completos (RAW)",
  "thresholds.summaryTokenThreshold": "⬆ más = tarda más pero ve más detalle",
  "thresholds.smartEventFilter": "Filtra stories por relevancia semántica",
  "sampling.segsPerStoryBase": "⬆ más = más fragmentos por story (más detalle)",
  "sampling.segsPerStoryMin": "⬆ más = garantiza detalle mínimo por story",
  "sampling.otfSegmentCap": "⬆ más = más testimonios reales en respuesta",
  "sampling.orphanCap": "⬆ más = más fragmentos sueltos, puede revelar conexiones",
  "sampling.coreRelevanceThreshold": "⬆ más = solo stories muy relevantes tienen datos completos",
  "sampling.segsPerCore": "⬆ más = más datos de stories principales (HYBRID)",
  "sampling.maxSegmentChars": "⬆ más = fragmentos más largos, contexto más pesado",
  "llm.model": "Modelo que genera la respuesta final",
  "llm.generatorTemperature": "⬆ más = más creativo pero impredecible",
  "prompts.systemPrompt": "Personalidad y reglas del motor de búsqueda",
  "prompts.userPromptTemplate": "Instrucciones que acompañan cada pregunta",
  "prompts.maxCitations": "⬆ más = más referencias verificables",
  "prompts.minCitationsBalance": "⬆ más = fuerza equilibrio logs/transcripciones",
  "prompts.maxContextTokens": "⬆ más = más datos pero más costoso",
  "limits.rateLimitPerMin": "⬆ más = permite más preguntas/minuto",
  "limits.dailyBudgetPerUser": "⬆ más = más gasto diario permitido",
  "limits.contextCacheTTL": "⬆ más = reutiliza contexto más tiempo",
  "limits.circuitBreakerCooldown": "⬆ más = espera más tras un error",
  pricing: "Costes por modelo — afecta estimación de gasto",
};
const getNestedValue = (obj: Record<string, any>, path: string): any => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

const setNestedValue = (
  obj: Record<string, any>,
  path: string,
  value: any,
): Record<string, any> => {
  const clone = JSON.parse(JSON.stringify(obj));
  const keys = path.split(".");
  let cursor = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!cursor[keys[i]]) cursor[keys[i]] = {};
    cursor = cursor[keys[i]];
  }
  cursor[keys[keys.length - 1]] = value;
  return clone;
};

// ─── Pipeline Node Definitions (Branching) ───
// topRow: linear flow (Estimation → Brain Selector → Mode Decision)
// branches: RAW / HYBRID / SUMMARY  (diverge from Mode Decision)
// bottomRow: convergence (Generation → Control)

const NODE_COLORS = {
  estimation: {
    color: "#a855f7",
    alpha: "rgba(168, 85, 247, 0.15)",
    border: "rgba(168, 85, 247, 0.35)",
  },
  brainSelector: {
    color: "#3b82f6",
    alpha: "rgba(59, 130, 246, 0.15)",
    border: "rgba(59, 130, 246, 0.35)",
  },
  modeDecision: {
    color: "#06b6d4",
    alpha: "rgba(6, 182, 212, 0.15)",
    border: "rgba(6, 182, 212, 0.35)",
  },
  raw: { color: "#22c55e", alpha: "rgba(34, 197, 94, 0.12)", border: "rgba(34, 197, 94, 0.30)" },
  hybrid: { color: "#eab308", alpha: "rgba(234, 179, 8, 0.12)", border: "rgba(234, 179, 8, 0.30)" },
  summary: {
    color: "#ef4444",
    alpha: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.30)",
  },
  generation: {
    color: "#ec4899",
    alpha: "rgba(236, 72, 153, 0.15)",
    border: "rgba(236, 72, 153, 0.35)",
  },
  control: {
    color: "#94a3b8",
    alpha: "rgba(148, 163, 184, 0.15)",
    border: "rgba(148, 163, 184, 0.35)",
  },
  loadingCaps: {
    color: "#f59e0b",
    alpha: "rgba(245, 158, 11, 0.15)",
    border: "rgba(245, 158, 11, 0.35)",
  },
};

const TOP_ROW_NODES = [
  {
    id: "brainSelector",
    step: 1,
    label: "brainSelector",
    icon: "bx-search-alt-2",
    ...NODE_COLORS.brainSelector,
    description: "preFiltersStories",
    categories: ["brainSelector"],
    // filterModel/filterTemperature/filterMaxTokens are displayed here but stored in llm.*
    extraParamKeys: ["llm.filterModel", "llm.filterTemperature", "llm.filterMaxTokens"],
  },
  {
    id: "modeDecision",
    step: 2,
    label: "modeDecision",
    icon: "bx-git-branch",
    ...NODE_COLORS.modeDecision,
    description: "rawHybridSummary",
    categories: ["thresholds"],
  },
];

const BRANCH_NODES = [
  {
    id: "summary",
    label: "summary",
    icon: "bx-file",
    ...NODE_COLORS.summary,
    subtitle: "summaryCondition",
    description: "summaryDesc",
    paramKeys: [],
  },
  {
    id: "hybrid",
    label: "hybrid",
    icon: "bx-git-merge-alt",
    ...NODE_COLORS.hybrid,
    subtitle: "hybridCondition",
    description: "hybridDesc",
    paramKeys: [
      "sampling.coreRelevanceThreshold",
      "sampling.segsPerCore",
      "sampling.otfSegmentCap",
      "sampling.orphanCap",
      "sampling.maxSegmentChars",
    ],
  },
  {
    id: "raw",
    label: "raw",
    icon: "bx-data",
    ...NODE_COLORS.raw,
    subtitle: "rawCondition",
    description: "rawDesc",
    paramKeys: [
      "sampling.segsPerStoryBase",
      "sampling.segsPerStoryMin",
      "sampling.otfSegmentCap",
      "sampling.maxSegmentChars",
    ],
  },
];

const BRANCH_TOOLTIPS: Record<string, string> = {
  summary: "summaryTooltip",
  hybrid: "hybridTooltip",
  raw: "rawTooltip",
};

const BOTTOM_ROW_NODES = [
  {
    id: "generation",
    step: 4,
    label: "generation",
    icon: "bx-brain",
    ...NODE_COLORS.generation,
    description: "generationDesc",
    categories: ["llm", "prompts"],
    excludeParamKeys: ["llm.filterModel", "llm.filterTemperature", "llm.filterMaxTokens"],
  },
];

const SIDE_NODES = [
  {
    id: "loadingCaps",
    step: "⚡",
    label: "loadingCaps",
    icon: "bx-slider",
    ...NODE_COLORS.loadingCaps,
    description: "loadingCapsDesc",
    categories: ["loadingCaps"],
  },
  {
    id: "estimation",
    step: "A",
    label: "estimation",
    icon: "bx-calculator",
    ...NODE_COLORS.estimation,
    description: "estimationDesc",
    categories: ["estimation"],
  },
  {
    id: "control",
    step: "B",
    label: "control",
    icon: "bx-shield-quarter",
    ...NODE_COLORS.control,
    description: "controlDesc",
    categories: ["limits"],
  },
];

// ─── Component ───
export const BrainEnginePanel: React.FC = () => {
  const { t } = useTranslation();
  const { activeProject } = useProjects();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [tooltips, setTooltips] = useState<Record<string, TooltipDef>>({});
  const [selectedPreset, setSelectedPreset] = useState<Preset | null>(null);
  const [editConfig, setEditConfig] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedTooltip, setExpandedTooltip] = useState<string | null>(null);
  const [activeInfoTooltip, setActiveInfoTooltip] = useState<string | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  // Config Assistant state
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantInput, setAssistantInput] = useState("");
  const [assistantMessages, setAssistantMessages] = useState<
    {
      role: "user" | "assistant";
      content: string;
      recommendations?: { param: string; value: any; reason: string }[];
    }[]
  >([]);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const assistantEndRef = useRef<HTMLDivElement>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  const [statusMessage, setStatusMessage] = useState<{
    text: string;
    type: "success" | "error" | "info";
  } | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [customInputs, setCustomInputs] = useState<Record<string, boolean>>({});
  const [renamingPresetId, setRenamingPresetId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  // Model Manager state
  const [showAddModel, setShowAddModel] = useState(false);
  const [newModelName, setNewModelName] = useState("");
  const [newModelInput, setNewModelInput] = useState("0.10");
  const [newModelOutput, setNewModelOutput] = useState("0.40");
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [editModelInput, setEditModelInput] = useState("");
  const [editModelOutput, setEditModelOutput] = useState("");

  // Project-preset mapping state
  const [projectMappings, setProjectMappings] = useState<Record<string, string>>({});
  const [projectsList, setProjectsList] = useState<{ id: string; name: string }[]>([]);

  // Help Modal state
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Helper: resolve dynamic options from pricing
  const getSelectOptions = (paramDef: TooltipDef): any[] => {
    if (paramDef.dynamicOptions === "pricing") {
      const pricing = editConfig?.pricing || {};
      const models = Object.keys(pricing);
      return [...(paramDef.prependOptions || []), ...models];
    }
    return paramDef.options || [];
  };

  // Speech-to-text state
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const [globalAlert, setGlobalAlert] = useState<{
    isOpen: boolean;
    message: string;
    title?: string;
    type?: "alert" | "prompt" | "confirm";
  } | null>(null);

  // ─── Load presets (global pool) ───
  const loadPresets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/brain/presets`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setPresets(data.presets || []);
      setTooltips(data.tooltips || {});
      setProjectMappings(data.projectMappings || {});
      setProjectsList(data.projects || []);
      // Auto-select default preset
      const def = data.presets?.find((p: Preset) => p.is_default);
      if (def) {
        setSelectedPreset(def);
        setEditConfig(JSON.parse(JSON.stringify(def.config)));
      }
    } catch (err: any) {
      setStatusMessage({ text: `Error: ${err.message}`, type: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  // ─── Save preset ───
  const savePreset = async () => {
    if (!selectedPreset) return;
    setSaving(true);
    try {
      const res = await authFetch(`/api/brain/presets/${selectedPreset.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config: editConfig }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setHasChanges(false);
      setStatusMessage({ text: t("admin.brainEngine.presetSaved"), type: "success" });
      await loadPresets();
    } catch (err: any) {
      setStatusMessage({ text: `Error: ${err.message}`, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ─── Set default preset (global fallback) ───
  const setDefaultPreset = async (preset: Preset) => {
    try {
      const res = await authFetch(`/api/brain/presets/${preset.id}/set-default`, {
        method: "POST",
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setStatusMessage({
        text: t("admin.brainEngine.presetDefault", { name: preset.name }),
        type: "success",
      });
      await loadPresets();
    } catch (err: any) {
      setStatusMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // ─── Duplicate preset ───
  const duplicatePreset = async (preset: Preset) => {
    try {
      const res = await authFetch(`/api/brain/presets/${preset.id}/duplicate`, { method: "POST" });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setStatusMessage({
        text: t("admin.brainEngine.presetDuplicated", { name: data.preset.name }),
        type: "success",
      });
      await loadPresets();
    } catch (err: any) {
      setStatusMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // ─── Assign preset to project ───
  const assignProjectPreset = async (projectId: string, presetId: string | null) => {
    try {
      const res = await authFetch(`/api/brain/project-presets/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ presetId }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setStatusMessage({
        text: presetId
          ? t("admin.brainEngine.presetAssigned")
          : t("admin.brainEngine.projectRestoredDefault"),
        type: "success",
      });
      await loadPresets();
    } catch (err: any) {
      setStatusMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // ─── Create preset ───
  const createPreset = async () => {
    if (!newPresetName.trim()) return;
    try {
      const res = await authFetch("/api/brain/presets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newPresetName.trim(),
          duplicateFrom: selectedPreset?.id,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setStatusMessage({
        text: t("admin.brainEngine.presetCreated", { name: newPresetName }),
        type: "success",
      });
      setNewPresetName("");
      setShowCreateDialog(false);
      await loadPresets();
    } catch (err: any) {
      setStatusMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // ─── Delete preset ───
  const deletePreset = async (preset: Preset) => {
    if (preset.is_default) return;
    try {
      const res = await authFetch(`/api/brain/presets/${preset.id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setStatusMessage({
        text: t("admin.brainEngine.presetDeleted", { name: preset.name }),
        type: "info",
      });
      await loadPresets();
    } catch (err: any) {
      setStatusMessage({ text: `❌ Error: ${err.message}`, type: "error" });
    }
  };

  // ─── Rename preset ───
  const renamePreset = async (presetId: string, newName: string) => {
    if (!newName.trim()) {
      setRenamingPresetId(null);
      return;
    }
    try {
      const res = await authFetch(`/api/brain/presets/${presetId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim() }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setStatusMessage({
        text: t("admin.brainEngine.presetRenamed", { name: newName.trim() }),
        type: "success",
      });
      setRenamingPresetId(null);
      await loadPresets();
    } catch (err: any) {
      setStatusMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // ─── Speech-to-Text (Microphone) ─────────────────────────
  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const startListening = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setGlobalAlert({
        isOpen: true,
        title: t("admin.brainEngine.attention"),
        message: t("admin.brainEngine.voiceDictationUnsupported"),
        type: "alert",
      });
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "es-ES";
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    let finalTranscript = assistantInput; // Append to existing text

    recognition.onresult = (event: any) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += (finalTranscript ? " " : "") + transcript;
        } else {
          interim = transcript;
        }
      }
      setAssistantInput(finalTranscript + (interim ? " " + interim : ""));
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  };

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, []);

  // ─── Handle param change ───
  const handleParamChange = (path: string, value: any) => {
    const newConfig = setNestedValue(editConfig, path, value);
    setEditConfig(newConfig);
    setHasChanges(true);
  };

  // Toggle category collapse
  const toggleCategoryCollapse = (cat: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  // Config Assistant: send message
  const sendAssistantMessage = async () => {
    if (!assistantInput.trim() || assistantLoading) return;
    const userMsg = assistantInput.trim();
    setAssistantInput("");
    setAssistantMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setAssistantLoading(true);

    try {
      const history = assistantMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const res = await authFetch("/api/brain/config-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userMsg,
          history,
          currentPresetId: selectedPreset?.id || undefined,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      setAssistantMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response?.message || "Sin respuesta",
          recommendations: data.response?.recommendations || [],
        },
      ]);
    } catch (err: any) {
      setAssistantMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${err.message}`,
        },
      ]);
    } finally {
      setAssistantLoading(false);
      setTimeout(() => assistantEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  // Apply a config recommendation directly to the editing config
  const applyRecommendation = (param: string, value: any) => {
    const newConfig = setNestedValue(editConfig, param, value);
    setEditConfig(newConfig);
    setHasChanges(true);
    // Visual feedback
    setAssistantMessages((prev) =>
      prev.map((m, i) => {
        if (i === prev.length - 1 && m.recommendations) {
          return {
            ...m,
            recommendations: m.recommendations.map((r) =>
              r.param === param ? { ...r, reason: r.reason + " (Aplicado)" } : r,
            ),
          };
        }
        return m;
      }),
    );
    setStatusMessage({ text: `${param} = ${value} — Guarda para aplicar`, type: "success" });
  };

  // ─── Clear status ───
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  // ─── Group tooltips by pipeline structure ───
  const topRowData = TOP_ROW_NODES.map((node) => {
    let params = Object.entries(tooltips).filter(([_, def]) =>
      node.categories.includes(def.category || ""),
    );
    // Add extra params (e.g. filterModel displayed in brainSelector)
    if ("extraParamKeys" in node && (node as any).extraParamKeys) {
      const extra = Object.entries(tooltips).filter(([key]) =>
        (node as any).extraParamKeys.includes(key),
      );
      params = [...params, ...extra];
    }
    // Force order: model first for brainSelector
    if (node.id === "brainSelector") {
      const preferredOrder = [
        "llm.filterModel",
        "brainSelector.enabled",
        "brainSelector.selectorTokenThreshold",
        "brainSelector.minStories",
        "llm.filterTemperature",
        "llm.filterMaxTokens",
        "brainSelector.catalogPrompt",
      ];
      params.sort((a, b) => {
        const ia = preferredOrder.indexOf(a[0]);
        const ib = preferredOrder.indexOf(b[0]);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });
    }
    return { ...node, params };
  });

  const branchData = BRANCH_NODES.map((branch) => ({
    ...branch,
    params: branch.paramKeys
      .map((key) => [key, tooltips[key]] as [string, TooltipDef])
      .filter(([_, def]) => def != null),
  }));

  const bottomRowData = BOTTOM_ROW_NODES.map((node) => {
    const excludeKeys = ("excludeParamKeys" in node && (node as any).excludeParamKeys) || [];
    return {
      ...node,
      params: Object.entries(tooltips).filter(
        ([key, def]) => node.categories.includes(def.category || "") && !excludeKeys.includes(key),
      ),
    };
  });

  const sideData = SIDE_NODES.map((node) => {
    return {
      ...node,
      params: Object.entries(tooltips).filter(([key, def]) =>
        node.categories.includes(def.category || ""),
      ),
    };
  });

  // ─── Info Tooltip helper ───
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
    showAbove: boolean;
  } | null>(null);

  // Close tooltip on outside click
  React.useEffect(() => {
    if (!activeInfoTooltip) return;
    const handler = () => {
      setActiveInfoTooltip(null);
      setTooltipPos(null);
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [activeInfoTooltip]);

  const handleInfoClick = (e: React.MouseEvent, paramKey: string) => {
    e.stopPropagation();
    if (activeInfoTooltip === paramKey) {
      setActiveInfoTooltip(null);
      setTooltipPos(null);
    } else {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const showAbove = spaceBelow < 200;
      setTooltipPos({
        top: showAbove ? rect.top : rect.bottom + 8,
        left: Math.min(rect.left, window.innerWidth - 300),
        showAbove,
      });
      setActiveInfoTooltip(paramKey);
    }
  };

  const renderInfoButton = (paramKey: string, accentColor: string = "var(--color-brand)") => {
    const tooltip = FRIENDLY_TOOLTIPS[paramKey];
    if (!tooltip) return null;
    return (
      <button
        onClick={(e) => handleInfoClick(e, paramKey)}
        style={{
          background: "none",
          border: "none",
          color: activeInfoTooltip === paramKey ? accentColor : "#666",
          cursor: "pointer",
          padding: "0 0 0 5px",
          fontSize: "0.75rem",
          display: "inline-flex",
          alignItems: "center",
          transition: "color 0.2s",
          flexShrink: 0,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = accentColor)}
        onMouseLeave={(e) => {
          if (activeInfoTooltip !== paramKey) e.currentTarget.style.color = "#666";
        }}
        title=""
      >
        <i className="bx bx-info-circle" />
      </button>
    );
  };

  // Rendered at top level, outside any overflow:hidden container
  const renderInfoTooltipOverlay = () => {
    if (!activeInfoTooltip || !tooltipPos) return null;

    // Support branch tooltips (branch.summary, branch.hybrid, branch.raw)
    let tooltip: string | undefined;
    if (activeInfoTooltip.startsWith("branch.")) {
      const branchId = activeInfoTooltip.replace("branch.", "");
      const tooltipKey = BRANCH_TOOLTIPS[branchId];
      tooltip = tooltipKey ? t(`admin.brainEngine.${tooltipKey}`) : undefined;
    } else {
      tooltip = FRIENDLY_TOOLTIPS[activeInfoTooltip];
    }
    if (!tooltip) return null;

    // Determine accent color from the param key prefix
    let accentColor = "var(--color-brand)";
    if (
      activeInfoTooltip.startsWith("brainSelector.") ||
      activeInfoTooltip.startsWith("llm.filter")
    )
      accentColor = NODE_COLORS.modeDecision?.color || "#06b6d4";
    else if (activeInfoTooltip.startsWith("sampling."))
      accentColor = NODE_COLORS.hybrid?.color || "#eab308";
    else if (activeInfoTooltip.startsWith("thresholds."))
      accentColor = NODE_COLORS.modeDecision?.color || "#06b6d4";
    else if (
      activeInfoTooltip.startsWith("llm.generator") ||
      activeInfoTooltip.startsWith("llm.model") ||
      activeInfoTooltip.startsWith("prompts.")
    )
      accentColor = NODE_COLORS.generation?.color || "#ec4899";
    else if (activeInfoTooltip.startsWith("estimation.")) accentColor = "#8b5cf6";
    else if (activeInfoTooltip.startsWith("limits.")) accentColor = "#06b6d4";
    else if (activeInfoTooltip === "branch.summary") accentColor = NODE_COLORS.summary.color;
    else if (activeInfoTooltip === "branch.hybrid") accentColor = NODE_COLORS.hybrid.color;
    else if (activeInfoTooltip === "branch.raw") accentColor = NODE_COLORS.raw.color;

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: tooltipPos.showAbove ? "auto" : `${tooltipPos.top}px`,
          bottom: tooltipPos.showAbove ? `${window.innerHeight - tooltipPos.top + 8}px` : "auto",
          left: `${Math.max(8, tooltipPos.left)}px`,
          background: "#1a1a2e",
          border: `1px solid ${accentColor}55`,
          borderRadius: "12px",
          padding: "14px 18px",
          width: "300px",
          zIndex: 10000,
          boxShadow: `0 12px 32px rgba(0,0,0,0.7), 0 0 0 1px ${accentColor}22, 0 0 20px ${accentColor}11`,
          fontSize: "0.74rem",
          color: "#d0d0d0",
          lineHeight: "1.55",
          fontWeight: 400,
          fontStyle: "normal",
          letterSpacing: "0",
          animation: "fadeIn 0.15s ease-out",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
          <i className="bx bx-info-circle" style={{ color: accentColor, fontSize: "0.9rem" }} />
          <span
            style={{
              fontSize: "0.65rem",
              fontWeight: 700,
              color: accentColor,
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Info
          </span>
          <button
            onClick={() => {
              setActiveInfoTooltip(null);
              setTooltipPos(null);
            }}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              color: "#666",
              cursor: "pointer",
              fontSize: "0.85rem",
              padding: "0",
              display: "flex",
            }}
          >
            <i className="bx bx-x" />
          </button>
        </div>
        {tooltip}
      </div>
    );
  };

  // State for textarea/JSON edit modal
  const [editingTextarea, setEditingTextarea] = useState<{ key: string; label: string } | null>(
    null,
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#888",
        }}
      >
        <i
          className="bx bx-loader-alt bx-spin"
          style={{ fontSize: "2rem", marginRight: "12px" }}
        ></i>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100%", gap: "20px", overflow: "hidden" }}>
      {/* ═══ LEFT SIDEBAR: Presets ═══ */}
      <div
        style={{
          width: "280px",
          minWidth: "280px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "var(--color-panel-bg)",
            border: "1px solid var(--color-panel-border)",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "0.9rem",
                fontWeight: 800,
                letterSpacing: "1px",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              PRESETS
              <button
                onClick={() => setShowHelpModal(true)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "rgba(255,255,255,0.4)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  transition: "color 0.2s",
                  fontSize: "1.2rem",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}
                title="Ver ayuda (Brain Engine Help)"
              >
                <i className="bx bx-help-circle"></i>
              </button>
            </h3>
            <button
              onClick={() => setShowCreateDialog(true)}
              style={{
                background: "rgba(var(--color-brand-rgb, 229, 62, 62), 0.15)",
                border: "1px solid rgba(var(--color-brand-rgb, 229, 62, 62), 0.3)",
                color: "var(--color-brand, #E53E3E)",
                borderRadius: "8px",
                padding: "6px 12px",
                fontSize: "0.75rem",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background =
                  "rgba(var(--color-brand-rgb, 229, 62, 62), 0.25)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  "rgba(var(--color-brand-rgb, 229, 62, 62), 0.15)")
              }
            >
              <i className="bx bx-plus"></i> NUEVO
            </button>
          </div>

          {/* Preset list */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {presets.map((preset) => (
              <div
                key={preset.id}
                onClick={() => {
                  setSelectedPreset(preset);
                  setEditConfig(JSON.parse(JSON.stringify(preset.config)));
                  setHasChanges(false);
                }}
                style={{
                  padding: "10px 12px",
                  borderRadius: "8px",
                  background:
                    selectedPreset?.id === preset.id
                      ? "rgba(var(--color-brand-rgb, 229, 62, 62), 0.15)"
                      : "rgba(255,255,255,0.03)",
                  border:
                    selectedPreset?.id === preset.id
                      ? "1px solid rgba(var(--color-brand-rgb, 229, 62, 62), 0.4)"
                      : "1px solid transparent",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    selectedPreset?.id === preset.id
                      ? "rgba(var(--color-brand-rgb, 229, 62, 62), 0.2)"
                      : "rgba(255,255,255,0.06)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    selectedPreset?.id === preset.id
                      ? "rgba(var(--color-brand-rgb, 229, 62, 62), 0.15)"
                      : "rgba(255,255,255,0.03)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {preset.is_default && (
                    <span
                      style={{
                        fontSize: "0.75rem",
                        flexShrink: 0,
                        color: "#f59e0b",
                        filter: "drop-shadow(0 0 4px rgba(245, 158, 11, 0.5))",
                      }}
                    >
                      ⭐
                    </span>
                  )}
                  {renamingPresetId === preset.id ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => renamePreset(preset.id, renameValue)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") renamePreset(preset.id, renameValue);
                        if (e.key === "Escape") setRenamingPresetId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#fff",
                        background: "rgba(255,255,255,0.1)",
                        border: "1px solid rgba(var(--color-brand-rgb, 229, 62, 62), 0.5)",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        outline: "none",
                        width: "100%",
                        minWidth: 0,
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: selectedPreset?.id === preset.id ? "#fff" : "#ccc",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {preset.name.replace(/^[^\w\s]+\s*/, "")}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenamingPresetId(preset.id);
                      setRenameValue(preset.name);
                    }}
                    title="Renombrar preset"
                    style={{
                      background: "rgba(255, 255, 255, 0.08)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      color: "#aaa",
                      borderRadius: "4px",
                      padding: "2px 6px",
                      fontSize: "0.65rem",
                      cursor: "pointer",
                    }}
                  >
                    <i className="bx bx-pencil"></i>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicatePreset(preset);
                    }}
                    title="Duplicar preset"
                    style={{
                      background: "rgba(59, 130, 246, 0.15)",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                      color: "#3b82f6",
                      borderRadius: "4px",
                      padding: "2px 6px",
                      fontSize: "0.65rem",
                      cursor: "pointer",
                    }}
                  >
                    <i className="bx bx-copy"></i>
                  </button>
                  {!preset.is_default && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDefaultPreset(preset);
                      }}
                      title="Marcar como default (global)"
                      style={{
                        background: "rgba(245, 158, 11, 0.15)",
                        border: "1px solid rgba(245, 158, 11, 0.3)",
                        color: "#f59e0b",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        fontSize: "0.65rem",
                        cursor: "pointer",
                      }}
                    >
                      <i className="bx bx-star"></i>
                    </button>
                  )}
                  {!preset.is_default && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePreset(preset);
                      }}
                      title="Eliminar preset"
                      style={{
                        background: "rgba(239, 68, 68, 0.15)",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                        color: "#ef4444",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        fontSize: "0.65rem",
                        cursor: "pointer",
                      }}
                    >
                      <i className="bx bx-trash"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ PROJECT PRESET ASSIGNMENT (in sidebar) ═══ */}
        {projectsList.length > 0 && (
          <div
            style={{
              background: "rgba(0,0,0,0.15)",
              border: "1px solid rgba(255,255,255,0.05)",
              borderRadius: "10px",
              padding: "14px",
              marginTop: "10px",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}
            >
              <i className="bx bx-folder-open" style={{ color: "#e91e8b", fontSize: "1rem" }}></i>
              <span
                style={{
                  fontWeight: 900,
                  letterSpacing: "0.5px",
                  fontSize: "0.75rem",
                  color: "#e91e8b",
                }}
              >
                PRESET POR PROYECTO
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {projectsList.map((proj) => {
                const assignedId = projectMappings[proj.id] || "";
                const defaultPreset = presets.find((p) => p.is_default);
                const currentLabel = assignedId
                  ? presets.find((p) => p.id === assignedId)?.name || "Preset"
                  : `⭐ Default${defaultPreset ? ` (${defaultPreset.name})` : ""}`;
                const isOpen = openDropdown === `proj-${proj.id}`;
                return (
                  <div key={proj.id} style={{ position: "relative" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          color: "#ccc",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {proj.name}
                      </span>
                      <button
                        onClick={() => setOpenDropdown(isOpen ? null : `proj-${proj.id}`)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "#1a1a2e",
                          border: "1px solid rgba(255,255,255,0.12)",
                          color: assignedId ? "#fff" : "#aaa",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          outline: "none",
                          width: "100%",
                          textAlign: "left",
                          transition: "border-color 0.2s",
                        }}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {currentLabel}
                        </span>
                        <i
                          className={`bx bx-chevron-${isOpen ? "up" : "down"}`}
                          style={{ fontSize: "0.85rem", flexShrink: 0 }}
                        ></i>
                      </button>
                    </div>
                    {isOpen && (
                      <>
                        <div
                          onClick={() => setOpenDropdown(null)}
                          style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 998,
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "#1a1a2e",
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: "8px",
                            marginTop: "4px",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                            zIndex: 999,
                            overflow: "hidden",
                            maxHeight: "180px",
                            overflowY: "auto",
                          }}
                        >
                          <div
                            onClick={() => {
                              assignProjectPreset(proj.id, null);
                              setOpenDropdown(null);
                            }}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              color: !assignedId ? "#e91e8b" : "#aaa",
                              background: !assignedId ? "rgba(233, 30, 139, 0.1)" : "transparent",
                              borderBottom: "1px solid rgba(255,255,255,0.05)",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = !assignedId
                                ? "rgba(233, 30, 139, 0.15)"
                                : "rgba(255,255,255,0.05)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = !assignedId
                                ? "rgba(233, 30, 139, 0.1)"
                                : "transparent")
                            }
                          >
                            ⭐ Default{defaultPreset ? ` (${defaultPreset.name})` : ""}
                          </div>
                          {presets.map((p) => (
                            <div
                              key={p.id}
                              onClick={() => {
                                assignProjectPreset(proj.id, p.id);
                                setOpenDropdown(null);
                              }}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                color: assignedId === p.id ? "#e91e8b" : "#ccc",
                                background:
                                  assignedId === p.id ? "rgba(233, 30, 139, 0.1)" : "transparent",
                                borderBottom: "1px solid rgba(255,255,255,0.03)",
                                transition: "background 0.15s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background =
                                  assignedId === p.id
                                    ? "rgba(233, 30, 139, 0.15)"
                                    : "rgba(255,255,255,0.05)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background =
                                  assignedId === p.id ? "rgba(233, 30, 139, 0.1)" : "transparent")
                              }
                            >
                              {p.is_default && "⭐ "}
                              {p.name}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ═══ MAIN CONTENT: Parameter Editor ═══ */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          gap: "12px",
        }}
      >
        {/* Top bar: preset name + save */}
        <div
          style={{
            background: "var(--color-panel-bg)",
            border: "1px solid var(--color-panel-border)",
            borderRadius: "12px",
            padding: "12px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <i
              className="bx bx-brain"
              style={{ fontSize: "1.5rem", color: "var(--color-brand, #E53E3E)" }}
            ></i>
            <div>
              <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#fff" }}>
                {selectedPreset?.name || "Selecciona un preset"}
              </h2>
              <span style={{ fontSize: "0.7rem", color: "#888" }}>
                {selectedPreset?.is_active ? "● ACTIVO" : "INACTIVO"}
                {hasChanges && " • Cambios sin guardar"}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {hasChanges && (
              <button
                onClick={() => {
                  if (selectedPreset) {
                    setEditConfig(JSON.parse(JSON.stringify(selectedPreset.config)));
                    setHasChanges(false);
                  }
                }}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid var(--color-panel-border)",
                  color: "#888",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                DESCARTAR
              </button>
            )}
            <button
              onClick={savePreset}
              disabled={!hasChanges || saving}
              style={{
                background: hasChanges ? "var(--color-brand, #E53E3E)" : "rgba(255,255,255,0.05)",
                border: "none",
                color: hasChanges ? "#fff" : "#666",
                borderRadius: "8px",
                padding: "8px 20px",
                fontSize: "0.8rem",
                fontWeight: 700,
                cursor: hasChanges ? "pointer" : "default",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s",
              }}
            >
              {saving ? (
                <i className="bx bx-loader-alt bx-spin"></i>
              ) : (
                <i className="bx bx-save"></i>
              )}
              {saving ? "GUARDANDO..." : "GUARDAR"}
            </button>
          </div>
        </div>

        {/* Status message */}
        {statusMessage && (
          <div
            style={{
              padding: "10px 16px",
              borderRadius: "8px",
              fontSize: "0.8rem",
              fontWeight: 600,
              background:
                statusMessage.type === "success"
                  ? "rgba(34, 197, 94, 0.1)"
                  : statusMessage.type === "error"
                    ? "rgba(239, 68, 68, 0.1)"
                    : "rgba(59, 130, 246, 0.1)",
              border: `1px solid ${
                statusMessage.type === "success"
                  ? "rgba(34, 197, 94, 0.3)"
                  : statusMessage.type === "error"
                    ? "rgba(239, 68, 68, 0.3)"
                    : "rgba(59, 130, 246, 0.3)"
              }`,
              color:
                statusMessage.type === "success"
                  ? "#22c55e"
                  : statusMessage.type === "error"
                    ? "#ef4444"
                    : "#3b82f6",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {statusMessage.type === "success" ? (
              <i className="bx bx-check-circle" style={{ fontSize: "1.2rem" }}></i>
            ) : statusMessage.type === "error" ? (
              <i className="bx bx-error-circle" style={{ fontSize: "1.2rem" }}></i>
            ) : (
              <i className="bx bx-info-circle" style={{ fontSize: "1.2rem" }}></i>
            )}
            {statusMessage.text}
          </div>
        )}

        {/* ═══ PIPELINE AND SETTINGS ═══ */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* TOP: Horizontal Pipeline Flow (Brain Selector → Mode Decision) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "20px 20px 0 20px",
              gap: "0",
              overflowX: "auto",
              minHeight: "min-content",
            }}
          >
            {/* ─── 1. TOP ROW (Brain Selector → Mode Decision) ─── */}
            {topRowData.map((node, idx) => (
              <React.Fragment key={node.id}>
                <div
                  style={{
                    width: "340px",
                    minWidth: "340px",
                    background: node.alpha,
                    border: `1px solid ${node.border}`,
                    borderRadius: "12px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "100%",
                    overflowY: "auto",
                    ...(node.id === "brainSelector" &&
                    !getNestedValue(editConfig, "brainSelector.enabled")
                      ? {
                          opacity: 0.45,
                          filter: "grayscale(0.8)",
                          transition: "opacity 0.3s, filter 0.3s",
                        }
                      : {
                          transition: "opacity 0.3s, filter 0.3s",
                        }),
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      background: `linear-gradient(135deg, ${node.alpha}, transparent)`,
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      borderBottom: `1px solid ${node.border}`,
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                    }}
                  >
                    <div
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "8px",
                        background: node.alpha,
                        border: `1px solid ${node.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className={`bx ${node.icon}`}
                        style={{ color: node.color, fontSize: "1.2rem" }}
                      ></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "0.88rem",
                          fontWeight: 800,
                          color: node.color,
                          letterSpacing: "0.5px",
                        }}
                      >
                        {node.step}. {t(`admin.brainEngine.${node.label}`)}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#666" }}>
                        {t(`admin.brainEngine.${node.description}`)}
                      </div>
                    </div>
                    {/* Toggle switch for Brain Selector */}
                    {node.id === "brainSelector" &&
                      (() => {
                        const isOn = !!getNestedValue(editConfig, "brainSelector.enabled");
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            {renderInfoButton("brainSelector.enabled", node.color)}
                            <div
                              onClick={() => handleParamChange("brainSelector.enabled", !isOn)}
                              style={{
                                width: "38px",
                                height: "20px",
                                borderRadius: "10px",
                                cursor: "pointer",
                                background: isOn ? node.color : "#333",
                                position: "relative",
                                transition: "background 0.25s ease",
                                border: `1px solid ${isOn ? node.color : "#555"}`,
                                flexShrink: 0,
                              }}
                            >
                              <div
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  borderRadius: "50%",
                                  background: "#fff",
                                  position: "absolute",
                                  top: "1px",
                                  left: isOn ? "19px" : "1px",
                                  transition: "left 0.25s ease",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                                }}
                              />
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                  {/* Params */}
                  <div
                    style={{
                      padding: "12px 14px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      flex: 1,
                    }}
                  >
                    {node.params.map(([paramKey, paramDef]: [string, TooltipDef]) => {
                      const currentValue = getNestedValue(editConfig, paramKey);
                      // Brain Selector enabled → skip (now in header)
                      if (paramKey === "brainSelector.enabled") {
                        return null;
                      }
                      // smartEventFilter → removed
                      if (paramKey === "thresholds.smartEventFilter") {
                        return null;
                      }
                      // Mode Decision: replace two threshold sliders with single dual slider
                      if (
                        node.id === "modeDecision" &&
                        paramKey === "thresholds.hybridTokenThreshold"
                      ) {
                        const hybridVal =
                          getNestedValue(editConfig, "thresholds.hybridTokenThreshold") || 30000;
                        const summaryVal =
                          getNestedValue(editConfig, "thresholds.summaryTokenThreshold") || 300000;
                        const MAX = 1000000;
                        const hybridPct = (hybridVal / MAX) * 100;
                        const summaryPct = (summaryVal / MAX) * 100;

                        return (
                          <div key="dual-threshold-slider">
                            {/* Title */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "10px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "0.78rem",
                                  fontWeight: 600,
                                  color: "#ccc",
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                Umbrales de Modo
                                {renderInfoButton("thresholds.hybridTokenThreshold", node.color)}
                              </span>
                            </div>

                            {/* Colored track + overlapping range inputs */}
                            <div
                              style={{ position: "relative", height: "28px", margin: "0 0 2px 0" }}
                            >
                              {/* 3-zone colored bar */}
                              <div
                                style={{
                                  position: "absolute",
                                  top: "10px",
                                  left: 0,
                                  right: 0,
                                  height: "8px",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  display: "flex",
                                }}
                              >
                                <div
                                  style={{
                                    width: `${hybridPct}%`,
                                    background: "linear-gradient(90deg, #16a34a, #22c55e, #4ade80)",
                                    transition: "width 0.1s",
                                  }}
                                />
                                <div
                                  style={{
                                    width: `${summaryPct - hybridPct}%`,
                                    background: "linear-gradient(90deg, #a16207, #eab308, #facc15)",
                                    transition: "width 0.1s",
                                  }}
                                />
                                <div
                                  style={{
                                    flex: 1,
                                    background: "linear-gradient(90deg, #dc2626, #ef4444, #f87171)",
                                  }}
                                />
                              </div>
                              {/* Hybrid handle */}
                              <input
                                type="range"
                                min={0}
                                max={MAX}
                                step={5000}
                                value={hybridVal}
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value);
                                  if (v < summaryVal - 5000)
                                    handleParamChange("thresholds.hybridTokenThreshold", v);
                                }}
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "28px",
                                  WebkitAppearance: "none",
                                  appearance: "none" as any,
                                  background: "transparent",
                                  pointerEvents: "none",
                                  zIndex: 2,
                                  margin: 0,
                                }}
                                className="dual-range-thumb"
                              />
                              {/* Summary handle */}
                              <input
                                type="range"
                                min={0}
                                max={MAX}
                                step={10000}
                                value={summaryVal}
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value);
                                  if (v > hybridVal + 5000)
                                    handleParamChange("thresholds.summaryTokenThreshold", v);
                                }}
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "28px",
                                  WebkitAppearance: "none",
                                  appearance: "none" as any,
                                  background: "transparent",
                                  pointerEvents: "none",
                                  zIndex: 3,
                                  margin: 0,
                                }}
                                className="dual-range-thumb"
                              />
                            </div>

                            {/* Value indicators positioned under handles */}
                            <div
                              style={{ position: "relative", height: "16px", marginBottom: "6px" }}
                            >
                              <span
                                style={{
                                  position: "absolute",
                                  left: `${hybridPct}%`,
                                  transform: "translateX(-50%)",
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  color: "#22c55e",
                                  background: "#22c55e22",
                                  padding: "1px 5px",
                                  borderRadius: "3px",
                                }}
                              >
                                {(hybridVal / 1000).toFixed(0)}K
                              </span>
                              <span
                                style={{
                                  position: "absolute",
                                  left: `${summaryPct}%`,
                                  transform: "translateX(-50%)",
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  color: "#eab308",
                                  background: "#eab30822",
                                  padding: "1px 5px",
                                  borderRadius: "3px",
                                }}
                              >
                                {(summaryVal / 1000).toFixed(0)}K
                              </span>
                            </div>

                            {/* Zone legend */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "0.58rem",
                                fontWeight: 600,
                                opacity: 0.85,
                              }}
                            >
                              <span
                                style={{
                                  color: "#4ade80",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <span
                                  style={{
                                    width: "7px",
                                    height: "7px",
                                    borderRadius: "50%",
                                    background:
                                      "radial-gradient(circle at 35% 35%, #86efac, #22c55e)",
                                    display: "inline-block",
                                    boxShadow: "0 0 4px #22c55e88",
                                  }}
                                />{" "}
                                RAW
                              </span>
                              <span
                                style={{
                                  color: "#facc15",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <span
                                  style={{
                                    width: "7px",
                                    height: "7px",
                                    borderRadius: "50%",
                                    background:
                                      "radial-gradient(circle at 35% 35%, #fde047, #eab308)",
                                    display: "inline-block",
                                    boxShadow: "0 0 4px #eab30888",
                                  }}
                                />{" "}
                                HYBRID
                              </span>
                              <span
                                style={{
                                  color: "#f87171",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <span
                                  style={{
                                    width: "7px",
                                    height: "7px",
                                    borderRadius: "50%",
                                    background:
                                      "radial-gradient(circle at 35% 35%, #fca5a5, #ef4444)",
                                    display: "inline-block",
                                    boxShadow: "0 0 4px #ef444488",
                                  }}
                                />{" "}
                                SUMMARY
                              </span>
                            </div>
                          </div>
                        );
                      }
                      // summaryTokenThreshold → skip (handled by dual slider above)
                      if (paramKey === "thresholds.summaryTokenThreshold") {
                        return null;
                      }
                      return (
                        <div key={paramKey}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: "5px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                color: "#ccc",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {paramDef.label}
                              {renderInfoButton(paramKey, node.color)}
                            </span>
                            {paramDef.type !== "textarea" && paramDef.type !== "json" && (
                              <span
                                style={{
                                  fontSize: "0.78rem",
                                  fontWeight: 800,
                                  fontFamily: "monospace",
                                  color: node.color,
                                  background: node.alpha,
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                              >
                                {currentValue == null || currentValue === ""
                                  ? "—"
                                  : typeof currentValue === "number"
                                    ? currentValue.toLocaleString()
                                    : String(currentValue)}
                              </span>
                            )}
                          </div>
                          {paramDef.type === "slider" && (
                            <input
                              type="range"
                              min={paramDef.min}
                              max={paramDef.max}
                              step={paramDef.step}
                              value={currentValue ?? paramDef.min}
                              onChange={(e) =>
                                handleParamChange(paramKey, parseFloat(e.target.value))
                              }
                              style={{
                                width: "100%",
                                accentColor: node.color,
                                height: "4px",
                                cursor: "pointer",
                              }}
                            />
                          )}
                          {paramDef.type === "number" && (
                            <input
                              type="number"
                              min={paramDef.min}
                              max={paramDef.max}
                              step={paramDef.step}
                              value={currentValue ?? 0}
                              onChange={(e) =>
                                handleParamChange(paramKey, parseFloat(e.target.value) || 0)
                              }
                              style={{
                                width: "100%",
                                padding: "4px 6px",
                                background: "rgba(255,255,255,0.04)",
                                border: `1px solid ${node.border}`,
                                borderRadius: "6px",
                                color: "#fff",
                                fontSize: "0.7rem",
                                fontFamily: "monospace",
                                outline: "none",
                              }}
                            />
                          )}
                          {paramDef.type === "select" && (
                            <div style={{ position: "relative" }}>
                              <div
                                onClick={() =>
                                  setOpenDropdown(openDropdown === paramKey ? null : paramKey)
                                }
                                style={{
                                  padding: "4px 6px",
                                  background: "rgba(255,255,255,0.04)",
                                  border: `1px solid ${node.border}`,
                                  borderRadius: "6px",
                                  color: "#fff",
                                  fontSize: "0.7rem",
                                  cursor: "pointer",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <span
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {currentValue == null ? "Seleccionar..." : String(currentValue)}
                                </span>
                                <i
                                  className={`bx bx-chevron-${openDropdown === paramKey ? "up" : "down"}`}
                                  style={{ fontSize: "0.8rem" }}
                                ></i>
                              </div>
                              {openDropdown === paramKey && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    marginTop: "2px",
                                    background: "var(--color-panel-bg)",
                                    border: "1px solid #333",
                                    borderRadius: "6px",
                                    zIndex: 100,
                                    overflow: "hidden",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                                  }}
                                >
                                  {getSelectOptions(paramDef).map((opt: any) => (
                                    <div
                                      key={String(opt)}
                                      onClick={() => {
                                        handleParamChange(paramKey, opt);
                                        setOpenDropdown(null);
                                      }}
                                      style={{
                                        padding: "6px 8px",
                                        fontSize: "0.7rem",
                                        cursor: "pointer",
                                        color: currentValue === opt ? node.color : "#fff",
                                        background:
                                          currentValue === opt ? node.alpha : "transparent",
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.currentTarget.style.background = node.alpha)
                                      }
                                      onMouseLeave={(e) =>
                                        (e.currentTarget.style.background =
                                          currentValue === opt ? node.alpha : "transparent")
                                      }
                                    >
                                      {String(opt)}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {paramDef.type === "textarea" && (
                            <div
                              onClick={() =>
                                setEditingTextarea({ key: paramKey, label: paramDef.label })
                              }
                              style={{
                                padding: "6px 8px",
                                background: "rgba(255,255,255,0.03)",
                                border: `1px solid ${node.border}`,
                                borderRadius: "6px",
                                color: "#999",
                                fontSize: "0.62rem",
                                fontFamily: "monospace",
                                cursor: "pointer",
                                maxHeight: "50px",
                                overflow: "hidden",
                                lineHeight: "1.3",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                              }}
                              title="Clic para editar"
                            >
                              {(currentValue || "").substring(0, 80)}...
                              <span style={{ color: node.color, fontWeight: 700 }}> ✎</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {node.params.length === 0 && (
                      <div
                        style={{
                          fontSize: "0.6rem",
                          color: "#555",
                          textAlign: "center",
                          padding: "6px",
                        }}
                      >
                        {t("admin.brainEngine.noParams")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Horizontal Arrow between top row nodes */}
                {idx < topRowData.length - 1 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: "80px",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="80" height="20" viewBox="0 0 80 20" style={{ display: "block" }}>
                      <line
                        x1="0"
                        y1="10"
                        x2="66"
                        y2="10"
                        stroke={node.color}
                        strokeWidth="2"
                        opacity="0.5"
                      />
                      <polygon
                        points="66,5 80,10 66,15"
                        fill={topRowData[idx + 1]?.color || "#666"}
                        opacity="0.5"
                      />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}

            {/* ═══ Branches with fork-line connectors ═══ */}

            {/* ─── Arrow from Mode Decision to left fork ─── */}
            <div style={{ display: "flex", alignItems: "center", minWidth: "60px" }}>
              <svg width="60" height="20" viewBox="0 0 60 20" style={{ display: "block" }}>
                <line
                  x1="0"
                  y1="10"
                  x2="46"
                  y2="10"
                  stroke={NODE_COLORS.modeDecision.color}
                  strokeWidth="2"
                  opacity="0.5"
                  strokeDasharray="6 4"
                />
                <polygon
                  points="46,5 60,10 46,15"
                  fill={NODE_COLORS.modeDecision.color}
                  opacity="0.5"
                />
              </svg>
            </div>

            {/* ─── Left Fork Line (vertical distribution) ─── */}
            <div style={{ display: "flex", alignItems: "center", minWidth: "3px" }}>
              <div
                style={{
                  width: "2px",
                  alignSelf: "stretch",
                  margin: "10px 0",
                  background: `linear-gradient(180deg, ${NODE_COLORS.summary?.color} 0%, ${NODE_COLORS.hybrid?.color} 50%, ${NODE_COLORS.raw?.color} 100%)`,
                  opacity: 0.6,
                  borderRadius: "2px",
                }}
              />
            </div>

            {/* ─── Branch Cards with short connectors ─── */}
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {branchData.map((branch) => (
                <div key={branch.id} style={{ display: "flex", alignItems: "center", gap: "0" }}>
                  {/* Short connector: left fork → card */}
                  <svg
                    width="24"
                    height="6"
                    viewBox="0 0 24 6"
                    style={{ display: "block", flexShrink: 0 }}
                  >
                    <line
                      x1="0"
                      y1="3"
                      x2="18"
                      y2="3"
                      stroke={branch.color}
                      strokeWidth="2"
                      opacity="0.5"
                    />
                    <polygon points="18,0.5 24,3 18,5.5" fill={branch.color} opacity="0.6" />
                  </svg>

                  {/* BRANCH CARD */}
                  <div
                    style={{
                      width: "390px",
                      minWidth: "390px",
                      background: branch.alpha,
                      border: `1px solid ${branch.border}`,
                      borderRadius: "10px",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        background: `linear-gradient(135deg, ${branch.alpha}, transparent)`,
                        padding: "10px 14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        borderBottom: `1px solid ${branch.border}`,
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          background: `radial-gradient(circle at 35% 35%, ${branch.color}aa, ${branch.color})`,
                          boxShadow: `0 0 10px ${branch.color}`,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: branch.color,
                            letterSpacing: "1px",
                          }}
                        >
                          {t(`admin.brainEngine.${branch.label.toLowerCase()}`)}
                        </div>
                        <div style={{ fontSize: "0.65rem", color: "#666" }}>
                          {t(`admin.brainEngine.${branch.subtitle}`)}
                        </div>
                      </div>
                      {/* Info tooltip for branch */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeInfoTooltip === `branch.${branch.id}`) {
                            setActiveInfoTooltip(null);
                          } else {
                            setActiveInfoTooltip(`branch.${branch.id}`);
                            const rect = (e.target as HTMLElement).getBoundingClientRect();
                            setTooltipPos({
                              top: rect.bottom + 8,
                              left: rect.left - 100,
                              showAbove: false,
                            });
                          }
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.75rem",
                          color:
                            activeInfoTooltip === `branch.${branch.id}` ? branch.color : "#555",
                          padding: "2px",
                          display: "flex",
                          alignItems: "center",
                          transition: "color 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = branch.color)}
                        onMouseLeave={(e) => {
                          if (activeInfoTooltip !== `branch.${branch.id}`)
                            e.currentTarget.style.color = "#555";
                        }}
                      >
                        <i className="bx bx-info-circle" />
                      </button>
                    </div>
                    {branch.params.length > 0 && (
                      <div
                        style={{
                          padding: "10px 14px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "7px",
                        }}
                      >
                        {branch.params.map(([paramKey, paramDef]) => {
                          if (!paramDef) return null;
                          const currentValue = getNestedValue(editConfig, paramKey);
                          return (
                            <div key={paramKey}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginBottom: "4px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: "#ccc",
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {paramDef.label}
                                  {renderInfoButton(paramKey, branch.color)}
                                </span>
                                <span
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: 800,
                                    fontFamily: "monospace",
                                    color: branch.color,
                                    background: branch.alpha,
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {currentValue == null
                                    ? "—"
                                    : typeof currentValue === "number"
                                      ? currentValue.toLocaleString()
                                      : String(currentValue)}
                                </span>
                              </div>
                              {paramDef.type === "slider" && (
                                <input
                                  type="range"
                                  min={paramDef.min}
                                  max={paramDef.max}
                                  step={paramDef.step}
                                  value={currentValue ?? paramDef.min}
                                  onChange={(e) =>
                                    handleParamChange(paramKey, parseFloat(e.target.value))
                                  }
                                  style={{
                                    width: "100%",
                                    accentColor: branch.color,
                                    height: "3px",
                                    cursor: "pointer",
                                  }}
                                />
                              )}
                              {paramDef.type === "number" && (
                                <input
                                  type="number"
                                  min={paramDef.min}
                                  max={paramDef.max}
                                  step={paramDef.step}
                                  value={currentValue ?? 0}
                                  onChange={(e) =>
                                    handleParamChange(paramKey, parseFloat(e.target.value) || 0)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "3px 5px",
                                    background: "rgba(255,255,255,0.04)",
                                    border: `1px solid ${branch.border}`,
                                    borderRadius: "5px",
                                    color: "#fff",
                                    fontSize: "0.62rem",
                                    fontFamily: "monospace",
                                    outline: "none",
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {branch.params.length === 0 && (
                      <div
                        style={{
                          padding: "10px",
                          fontSize: "0.55rem",
                          color: "#666",
                          textAlign: "center",
                        }}
                      >
                        Sin muestreo — usa descripciones
                      </div>
                    )}
                  </div>

                  {/* Short connector: card → right fork */}
                  <svg
                    width="24"
                    height="6"
                    viewBox="0 0 24 6"
                    style={{ display: "block", flexShrink: 0 }}
                  >
                    <line
                      x1="0"
                      y1="3"
                      x2="18"
                      y2="3"
                      stroke={branch.color}
                      strokeWidth="2"
                      opacity="0.5"
                    />
                    <polygon
                      points="18,0.5 24,3 18,5.5"
                      fill={NODE_COLORS.generation.color}
                      opacity="0.6"
                    />
                  </svg>
                </div>
              ))}
            </div>

            {/* ─── Right Fork Line (vertical convergence) ─── */}
            <div style={{ display: "flex", alignItems: "center", minWidth: "3px" }}>
              <div
                style={{
                  width: "2px",
                  alignSelf: "stretch",
                  margin: "10px 0",
                  background: `linear-gradient(180deg, ${NODE_COLORS.summary?.color} 0%, ${NODE_COLORS.hybrid?.color} 50%, ${NODE_COLORS.raw?.color} 100%)`,
                  opacity: 0.6,
                  borderRadius: "2px",
                }}
              />
            </div>

            {/* ─── Arrow from right fork to Generation ─── */}
            <div style={{ display: "flex", alignItems: "center", minWidth: "60px" }}>
              <svg width="60" height="20" viewBox="0 0 60 20" style={{ display: "block" }}>
                <line
                  x1="0"
                  y1="10"
                  x2="46"
                  y2="10"
                  stroke={NODE_COLORS.generation.color}
                  strokeWidth="2"
                  opacity="0.5"
                  strokeDasharray="6 4"
                />
                <polygon
                  points="46,5 60,10 46,15"
                  fill={NODE_COLORS.generation.color}
                  opacity="0.5"
                />
              </svg>
            </div>

            {/* ─── GENERATION NODE (to the right) ─── */}
            <div style={{ display: "flex", alignItems: "center" }}>
              {bottomRowData.map((node) => (
                <div
                  key={node.id}
                  style={{
                    width: "360px",
                    minWidth: "360px",
                    background: node.alpha,
                    border: `1px solid ${node.border}`,
                    borderRadius: "12px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "100%",
                    overflowY: "auto",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      background: `linear-gradient(135deg, ${node.alpha}, transparent)`,
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      borderBottom: `1px solid ${node.border}`,
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                    }}
                  >
                    <div
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "8px",
                        background: node.alpha,
                        border: `1px solid ${node.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className={`bx ${node.icon}`}
                        style={{ color: node.color, fontSize: "1.2rem" }}
                      ></i>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.88rem",
                          fontWeight: 800,
                          color: node.color,
                          letterSpacing: "0.5px",
                        }}
                      >
                        {node.step}. {t(`admin.brainEngine.${node.label}`)}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#666" }}>
                        {t(`admin.brainEngine.${node.description}`)}
                      </div>
                    </div>
                  </div>
                  {/* Params */}
                  <div
                    style={{
                      padding: "12px 14px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {node.params.map(([paramKey, paramDef]: [string, TooltipDef]) => {
                      const currentValue = getNestedValue(editConfig, paramKey);
                      return (
                        <div key={paramKey}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: "4px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.62rem",
                                fontWeight: 600,
                                color: "#ccc",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {paramDef.label}
                              {renderInfoButton(paramKey, node.color)}
                            </span>
                            {paramDef.type !== "textarea" && paramDef.type !== "json" && (
                              <span
                                style={{
                                  fontSize: "0.62rem",
                                  fontWeight: 800,
                                  fontFamily: "monospace",
                                  color: node.color,
                                  background: node.alpha,
                                  padding: "1px 5px",
                                  borderRadius: "4px",
                                }}
                              >
                                {currentValue == null || currentValue === ""
                                  ? "—"
                                  : typeof currentValue === "number"
                                    ? currentValue.toLocaleString()
                                    : String(currentValue)}
                              </span>
                            )}
                          </div>
                          {paramDef.type === "slider" && (
                            <input
                              type="range"
                              min={paramDef.min}
                              max={paramDef.max}
                              step={paramDef.step}
                              value={currentValue ?? paramDef.min}
                              onChange={(e) =>
                                handleParamChange(paramKey, parseFloat(e.target.value))
                              }
                              style={{
                                width: "100%",
                                accentColor: node.color,
                                height: "4px",
                                cursor: "pointer",
                              }}
                            />
                          )}
                          {paramDef.type === "number" && (
                            <input
                              type="number"
                              min={paramDef.min}
                              max={paramDef.max}
                              step={paramDef.step}
                              value={currentValue ?? 0}
                              onChange={(e) =>
                                handleParamChange(paramKey, parseFloat(e.target.value) || 0)
                              }
                              style={{
                                width: "100%",
                                padding: "4px 6px",
                                background: "rgba(255,255,255,0.04)",
                                border: `1px solid ${node.border}`,
                                borderRadius: "6px",
                                color: "#fff",
                                fontSize: "0.68rem",
                                fontFamily: "monospace",
                                outline: "none",
                              }}
                            />
                          )}
                          {paramDef.type === "select" && (
                            <div style={{ position: "relative" }}>
                              <div
                                onClick={() =>
                                  setOpenDropdown(openDropdown === paramKey ? null : paramKey)
                                }
                                style={{
                                  padding: "4px 6px",
                                  background: "rgba(255,255,255,0.04)",
                                  border: `1px solid ${node.border}`,
                                  borderRadius: "6px",
                                  color: "#fff",
                                  fontSize: "0.68rem",
                                  cursor: "pointer",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <span
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {currentValue == null ? "Seleccionar..." : String(currentValue)}
                                </span>
                                <i
                                  className={`bx bx-chevron-${openDropdown === paramKey ? "up" : "down"}`}
                                  style={{ fontSize: "0.8rem" }}
                                ></i>
                              </div>
                              {openDropdown === paramKey && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    marginTop: "2px",
                                    background: "var(--color-panel-bg)",
                                    border: "1px solid #333",
                                    borderRadius: "6px",
                                    zIndex: 100,
                                    maxHeight: "240px",
                                    overflowY: "auto",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                                  }}
                                >
                                  {getSelectOptions(paramDef).map((opt: any) => (
                                    <div
                                      key={String(opt)}
                                      onClick={() => {
                                        handleParamChange(paramKey, opt);
                                        setOpenDropdown(null);
                                      }}
                                      style={{
                                        padding: "6px 8px",
                                        fontSize: "0.68rem",
                                        cursor: "pointer",
                                        color: currentValue === opt ? node.color : "#fff",
                                        background:
                                          currentValue === opt ? node.alpha : "transparent",
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.currentTarget.style.background = node.alpha)
                                      }
                                      onMouseLeave={(e) =>
                                        (e.currentTarget.style.background =
                                          currentValue === opt ? node.alpha : "transparent")
                                      }
                                    >
                                      {String(opt)}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {paramDef.type === "textarea" && (
                            <div
                              onClick={() =>
                                setEditingTextarea({ key: paramKey, label: paramDef.label })
                              }
                              style={{
                                padding: "5px 7px",
                                background: "rgba(255,255,255,0.03)",
                                border: `1px solid ${node.border}`,
                                borderRadius: "6px",
                                color: "#999",
                                fontSize: "0.58rem",
                                fontFamily: "monospace",
                                cursor: "pointer",
                                maxHeight: "80px",
                                overflow: "hidden",
                                lineHeight: "1.3",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                              }}
                              title="Clic para editar"
                            >
                              {(currentValue || "").substring(0, 120)}...
                              <span style={{ color: node.color, fontWeight: 700 }}> ✎</span>
                            </div>
                          )}
                          {paramDef.type === "json" && (
                            <div
                              onClick={() =>
                                setEditingTextarea({ key: paramKey, label: paramDef.label })
                              }
                              style={{
                                padding: "5px 7px",
                                background: "rgba(255,255,255,0.03)",
                                border: `1px solid ${node.border}`,
                                borderRadius: "6px",
                                color: "#999",
                                fontSize: "0.58rem",
                                fontFamily: "monospace",
                                cursor: "pointer",
                                maxHeight: "80px",
                                overflow: "hidden",
                                lineHeight: "1.3",
                                whiteSpace: "pre",
                              }}
                              title="Clic para editar JSON"
                            >
                              {JSON.stringify(currentValue, null, 1)?.substring(0, 120)}...
                              <span style={{ color: node.color, fontWeight: 700 }}> ✎</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* End unified horizontal pipeline row */}

          {/* ─── BOTTOM: Side Nodes (Estimation & Control) ─── */}
          <div style={{ width: "100%", height: "1px", background: "rgba(255,255,255,0.06)" }} />
          <div
            style={{
              padding: "12px 20px",
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontSize: "0.8rem",
                fontWeight: 800,
                color: "#aaa",
                letterSpacing: "1px",
                width: "100%",
              }}
            >
              {t("admin.brainEngine.controlAndEstimation")}
            </div>
            {sideData.map((node) => (
              <div
                key={node.id}
                style={{
                  flex: "1 1 300px",
                  minWidth: "300px",
                  background: node.alpha,
                  border: `1px solid ${node.border}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "row",
                  flexShrink: 0,
                }}
              >
                {/* Header (Left side of landscape box) */}
                <div
                  style={{
                    background: `linear-gradient(135deg, ${node.alpha}, transparent)`,
                    padding: "14px 16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    borderRight: `1px solid ${node.border}`,
                    width: "160px",
                    minWidth: "160px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: node.alpha,
                      border: `1px solid ${node.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className={`bx ${node.icon}`}
                      style={{ color: node.color, fontSize: "1.1rem" }}
                    ></i>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 800,
                        color: node.color,
                        letterSpacing: "0.5px",
                      }}
                    >
                      {t(`admin.brainEngine.${node.label}`)}
                    </div>
                    <div style={{ fontSize: "0.65rem", color: "#888", marginTop: "2px" }}>
                      {t(`admin.brainEngine.${node.description}`)}
                    </div>
                  </div>
                </div>
                {/* Params (Grid Layout) */}
                <div
                  style={{
                    padding: "12px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                    gap: "10px",
                    flex: 1,
                    alignContent: "start",
                  }}
                >
                  {node.params.map(([paramKey, paramDef]: [string, TooltipDef]) => {
                    const currentValue = getNestedValue(editConfig, paramKey);
                    return (
                      <div
                        key={paramKey}
                        style={{
                          background: "rgba(0,0,0,0.15)",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid rgba(255,255,255,0.03)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "6px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              color: "#ccc",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            {paramDef.label}
                            {renderInfoButton(paramKey, node.color)}
                          </span>
                          {paramDef.type !== "textarea" && paramDef.type !== "json" && (
                            <span
                              style={{
                                fontSize: "0.65rem",
                                fontWeight: 800,
                                fontFamily: "monospace",
                                color: node.color,
                                background: node.alpha,
                                padding: "2px 5px",
                                borderRadius: "4px",
                              }}
                            >
                              {currentValue == null || currentValue === ""
                                ? "—"
                                : typeof currentValue === "number"
                                  ? currentValue.toLocaleString()
                                  : String(currentValue)}
                            </span>
                          )}
                        </div>
                        {paramDef.type === "slider" && (
                          <input
                            type="range"
                            min={paramDef.min}
                            max={paramDef.max}
                            step={paramDef.step}
                            value={currentValue ?? paramDef.min}
                            onChange={(e) =>
                              handleParamChange(paramKey, parseFloat(e.target.value))
                            }
                            style={{
                              width: "100%",
                              accentColor: node.color,
                              height: "4px",
                              cursor: "pointer",
                              marginTop: "4px",
                            }}
                          />
                        )}
                        {paramDef.type === "number" && (
                          <input
                            type="number"
                            min={paramDef.min}
                            max={paramDef.max}
                            step={paramDef.step}
                            value={currentValue ?? 0}
                            onChange={(e) =>
                              handleParamChange(paramKey, parseFloat(e.target.value) || 0)
                            }
                            style={{
                              width: "100%",
                              padding: "6px 8px",
                              background: "rgba(255,255,255,0.04)",
                              border: `1px solid ${node.border}`,
                              borderRadius: "6px",
                              color: "#fff",
                              fontSize: "0.75rem",
                              fontFamily: "monospace",
                              outline: "none",
                            }}
                          />
                        )}
                        {paramDef.type === "select" && (
                          <div style={{ position: "relative" }}>
                            <div
                              onClick={() =>
                                setOpenDropdown(openDropdown === paramKey ? null : paramKey)
                              }
                              style={{
                                padding: "6px 8px",
                                background: "rgba(255,255,255,0.04)",
                                border: `1px solid ${node.border}`,
                                borderRadius: "6px",
                                color: "#fff",
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {currentValue == null ? "Seleccionar..." : String(currentValue)}
                              </span>
                              <i
                                className={`bx bx-chevron-${openDropdown === paramKey ? "up" : "down"}`}
                                style={{ fontSize: "0.9rem" }}
                              ></i>
                            </div>
                            {openDropdown === paramKey && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  marginTop: "2px",
                                  background: "var(--color-panel-bg)",
                                  border: "1px solid #333",
                                  borderRadius: "6px",
                                  zIndex: 100,
                                  overflow: "hidden",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                                }}
                              >
                                {getSelectOptions(paramDef).map((opt: any) => (
                                  <div
                                    key={String(opt)}
                                    onClick={() => {
                                      handleParamChange(paramKey, opt);
                                      setOpenDropdown(null);
                                    }}
                                    style={{
                                      padding: "8px 10px",
                                      fontSize: "0.75rem",
                                      cursor: "pointer",
                                      color: currentValue === opt ? node.color : "#fff",
                                      background: currentValue === opt ? node.alpha : "transparent",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.background = node.alpha)
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.background =
                                        currentValue === opt ? node.alpha : "transparent")
                                    }
                                  >
                                    {String(opt)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {paramDef.type === "textarea" && (
                          <div
                            onClick={() =>
                              setEditingTextarea({ key: paramKey, label: paramDef.label })
                            }
                            style={{
                              padding: "6px 8px",
                              background: "rgba(255,255,255,0.03)",
                              border: `1px solid ${node.border}`,
                              borderRadius: "6px",
                              color: "#999",
                              fontSize: "0.65rem",
                              fontFamily: "monospace",
                              cursor: "pointer",
                              maxHeight: "50px",
                              overflow: "hidden",
                              lineHeight: "1.3",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-all",
                            }}
                            title="Clic para editar"
                          >
                            {(currentValue || "").substring(0, 50)}...
                            <span style={{ color: node.color, fontWeight: 700 }}> ✎</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {node.params.length === 0 && (
                    <div
                      style={{
                        fontSize: "0.6rem",
                        color: "#555",
                        textAlign: "center",
                        padding: "16px",
                      }}
                    >
                      {t("admin.brainEngine.noConfigurableParams")}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ═══ MODEL MANAGER ═══ */}
          <div style={{ width: "100%", height: "1px", background: "rgba(255,255,255,0.06)" }} />
          <div style={{ padding: "12px 20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background: "rgba(233, 30, 139, 0.08)",
                    border: "1px solid rgba(233, 30, 139, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="bx bx-chip" style={{ color: "#e91e8b", fontSize: "1.1rem" }}></i>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 800,
                      color: "#e91e8b",
                      letterSpacing: "0.5px",
                    }}
                  >
                    GESTOR DE MODELOS
                  </div>
                  <div style={{ fontSize: "0.65rem", color: "#888", marginTop: "1px" }}>
                    Costes por modelo ($/1M tokens)
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAddModel(true);
                  setNewModelName("");
                  setNewModelInput("0.10");
                  setNewModelOutput("0.40");
                }}
                style={{
                  padding: "6px 14px",
                  borderRadius: "8px",
                  border: "1px solid rgba(233, 30, 139, 0.3)",
                  background: "rgba(233, 30, 139, 0.08)",
                  color: "#e91e8b",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <i className="bx bx-plus" style={{ fontSize: "0.9rem" }}></i> Añadir modelo
              </button>
            </div>

            {/* Model Table */}
            <div
              style={{
                background: "rgba(0,0,0,0.2)",
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.05)",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 120px 120px 60px",
                  padding: "8px 14px",
                  background: "rgba(255,255,255,0.03)",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <span
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Modelo
                </span>
                <span
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: "center",
                  }}
                >
                  Input $/1M
                </span>
                <span
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: "center",
                  }}
                >
                  Output $/1M
                </span>
                <span
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: "center",
                  }}
                ></span>
              </div>
              {/* Rows */}
              {Object.entries(editConfig?.pricing || {}).map(
                ([modelName, prices]: [string, any]) => (
                  <div
                    key={modelName}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 120px 120px 60px",
                      padding: "8px 14px",
                      borderBottom: "1px solid rgba(255,255,255,0.03)",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#ddd",
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {editConfig?.llm?.model === modelName && (
                        <i
                          className="bx bxs-star"
                          style={{ color: "#e91e8b", marginRight: "4px", fontSize: "0.65rem" }}
                          title="Modelo generador activo"
                        ></i>
                      )}
                      {modelName}
                    </span>
                    {editingModel === modelName ? (
                      <>
                        <input
                          type="number"
                          step="0.01"
                          value={editModelInput}
                          onChange={(e) => setEditModelInput(e.target.value)}
                          style={{
                            width: "90px",
                            margin: "0 auto",
                            padding: "3px 6px",
                            background: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(233, 30, 139, 0.4)",
                            borderRadius: "4px",
                            color: "#fff",
                            fontSize: "0.7rem",
                            fontFamily: "monospace",
                            textAlign: "center",
                            outline: "none",
                          }}
                        />
                        <input
                          type="number"
                          step="0.01"
                          value={editModelOutput}
                          onChange={(e) => setEditModelOutput(e.target.value)}
                          style={{
                            width: "90px",
                            margin: "0 auto",
                            padding: "3px 6px",
                            background: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(233, 30, 139, 0.4)",
                            borderRadius: "4px",
                            color: "#fff",
                            fontSize: "0.7rem",
                            fontFamily: "monospace",
                            textAlign: "center",
                            outline: "none",
                          }}
                        />
                        <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                          <button
                            onClick={() => {
                              const updated = { ...editConfig };
                              updated.pricing = {
                                ...updated.pricing,
                                [modelName]: {
                                  input: parseFloat(editModelInput) || 0,
                                  output: parseFloat(editModelOutput) || 0,
                                },
                              };
                              setEditConfig(updated);
                              setHasChanges(true);
                              setEditingModel(null);
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#4ade80",
                              fontSize: "0.9rem",
                              padding: "2px",
                            }}
                            title="Guardar"
                          >
                            <i className="bx bx-check"></i>
                          </button>
                          <button
                            onClick={() => setEditingModel(null)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#888",
                              fontSize: "0.9rem",
                              padding: "2px",
                            }}
                            title="Cancelar"
                          >
                            <i className="bx bx-x"></i>
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "#aaa",
                            fontFamily: "monospace",
                            textAlign: "center",
                          }}
                        >
                          ${prices?.input?.toFixed(2) || "—"}
                        </span>
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "#aaa",
                            fontFamily: "monospace",
                            textAlign: "center",
                          }}
                        >
                          ${prices?.output?.toFixed(2) || "—"}
                        </span>
                        <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                          <button
                            onClick={() => {
                              setEditingModel(modelName);
                              setEditModelInput(String(prices?.input || 0));
                              setEditModelOutput(String(prices?.output || 0));
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#888",
                              fontSize: "0.85rem",
                              padding: "2px",
                            }}
                            title="Editar precios"
                          >
                            <i className="bx bx-edit-alt"></i>
                          </button>
                          <button
                            onClick={() => {
                              if (editConfig?.llm?.model === modelName) {
                                setStatusMessage({
                                  text: "No puedes eliminar el modelo activo",
                                  type: "error",
                                });
                                return;
                              }
                              const updated = { ...editConfig };
                              const newPricing = { ...updated.pricing };
                              delete newPricing[modelName];
                              updated.pricing = newPricing;
                              setEditConfig(updated);
                              setHasChanges(true);
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#666",
                              fontSize: "0.85rem",
                              padding: "2px",
                            }}
                            title="Eliminar"
                          >
                            <i className="bx bx-trash"></i>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ),
              )}
              {Object.keys(editConfig?.pricing || {}).length === 0 && (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#555",
                    fontSize: "0.7rem",
                  }}
                >
                  No hay modelos configurados
                </div>
              )}
            </div>

            {/* Add Model Dialog (inline) */}
            {showAddModel && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "12px",
                  background: "rgba(233, 30, 139, 0.05)",
                  border: "1px solid rgba(233, 30, 139, 0.2)",
                  borderRadius: "10px",
                  display: "flex",
                  gap: "8px",
                  alignItems: "flex-end",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: "2 1 200px" }}>
                  <label
                    style={{
                      fontSize: "0.6rem",
                      color: "#888",
                      display: "block",
                      marginBottom: "3px",
                    }}
                  >
                    Nombre del modelo
                  </label>
                  <input
                    type="text"
                    value={newModelName}
                    onChange={(e) => setNewModelName(e.target.value)}
                    placeholder="gemini-2.5-pro"
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(233, 30, 139, 0.3)",
                      borderRadius: "6px",
                      color: "#fff",
                      fontSize: "0.75rem",
                      fontFamily: "monospace",
                      outline: "none",
                    }}
                  />
                </div>
                <div style={{ flex: "1 1 80px" }}>
                  <label
                    style={{
                      fontSize: "0.6rem",
                      color: "#888",
                      display: "block",
                      marginBottom: "3px",
                    }}
                  >
                    Input $/1M
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newModelInput}
                    onChange={(e) => setNewModelInput(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(233, 30, 139, 0.3)",
                      borderRadius: "6px",
                      color: "#fff",
                      fontSize: "0.75rem",
                      fontFamily: "monospace",
                      outline: "none",
                    }}
                  />
                </div>
                <div style={{ flex: "1 1 80px" }}>
                  <label
                    style={{
                      fontSize: "0.6rem",
                      color: "#888",
                      display: "block",
                      marginBottom: "3px",
                    }}
                  >
                    Output $/1M
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newModelOutput}
                    onChange={(e) => setNewModelOutput(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(233, 30, 139, 0.3)",
                      borderRadius: "6px",
                      color: "#fff",
                      fontSize: "0.75rem",
                      fontFamily: "monospace",
                      outline: "none",
                    }}
                  />
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    onClick={() => {
                      if (!newModelName.trim()) return;
                      const updated = { ...editConfig };
                      updated.pricing = {
                        ...updated.pricing,
                        [newModelName.trim()]: {
                          input: parseFloat(newModelInput) || 0,
                          output: parseFloat(newModelOutput) || 0,
                        },
                      };
                      setEditConfig(updated);
                      setHasChanges(true);
                      setShowAddModel(false);
                      setNewModelName("");
                    }}
                    style={{
                      padding: "6px 14px",
                      borderRadius: "6px",
                      border: "none",
                      background: "#e91e8b",
                      color: "#fff",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Añadir
                  </button>
                  <button
                    onClick={() => setShowAddModel(false)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: "6px",
                      border: "1px solid #444",
                      background: "transparent",
                      color: "#888",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* ═══ API KEY (inside Model Manager) ═══ */}
            <div style={{ marginTop: "12px" }}>
              <ApiKeyManager />
            </div>
          </div>
        </div>
        {/* ═══ CONFIG ASSISTANT ═══ */}
        <div
          style={{
            background: "var(--color-panel-bg)",
            border: assistantOpen
              ? "1px solid rgba(var(--color-brand-rgb, 229, 62, 62), 0.3)"
              : "1px solid var(--color-panel-border)",
            borderRadius: "12px",
            padding: "12px 16px",
            transition: "all 0.3s",
          }}
        >
          <div
            onClick={() => setAssistantOpen(!assistantOpen)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i
                className="bx bx-bot"
                style={{ fontSize: "1.1rem", color: "var(--color-brand, #E53E3E)" }}
              ></i>
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 800,
                  letterSpacing: "0.5px",
                  color: "#aaa",
                }}
              >
                CONFIG ASSISTANT
              </span>
              <span style={{ fontSize: "0.65rem", color: "#555" }}>
                Describe el tipo de respuesta que quieres y te recomiendo los parametros
              </span>
            </div>
            <i
              className={`bx bx-chevron-${assistantOpen ? "down" : "right"}`}
              style={{ fontSize: "1.1rem", color: "#666", transition: "transform 0.2s" }}
            ></i>
          </div>

          {assistantOpen && (
            <div style={{ marginTop: "12px" }}>
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "12px",
                  padding: "4px 0",
                }}
              >
                {assistantMessages.length === 0 && (
                  <div
                    style={{
                      color: "#555",
                      fontSize: "0.75rem",
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                    Pregunta algo como: "Quiero respuestas mas cortas y directas" o "Necesito mas
                    citas de transcripciones"
                  </div>
                )}
                {assistantMessages.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "80%",
                        background:
                          msg.role === "user"
                            ? "rgba(var(--color-brand-rgb, 229, 62, 62), 0.15)"
                            : "rgba(255,255,255,0.05)",
                        border:
                          msg.role === "user"
                            ? "1px solid rgba(var(--color-brand-rgb, 229, 62, 62), 0.25)"
                            : "1px solid rgba(255,255,255,0.08)",
                        borderRadius:
                          msg.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                        padding: "10px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.78rem",
                          color: "#ddd",
                          lineHeight: "1.5",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {msg.content}
                      </div>
                      {msg.recommendations && msg.recommendations.length > 0 && (
                        <div
                          style={{
                            marginTop: "10px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                            borderTop: "1px solid rgba(255,255,255,0.06)",
                            paddingTop: "10px",
                          }}
                        >
                          {msg.recommendations.map((rec, ri) => (
                            <div
                              key={ri}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                background: "rgba(255,255,255,0.03)",
                                borderRadius: "8px",
                                padding: "8px 12px",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: "0.72rem",
                                    color: "var(--color-brand, #E53E3E)",
                                    fontWeight: 700,
                                  }}
                                >
                                  {rec.param} ={" "}
                                  {typeof rec.value === "number"
                                    ? rec.value
                                    : JSON.stringify(rec.value)}
                                </div>
                                <div
                                  style={{ fontSize: "0.68rem", color: "#888", marginTop: "2px" }}
                                >
                                  {rec.reason}
                                </div>
                              </div>
                              {!rec.reason.includes("aplicado") && (
                                <button
                                  onClick={() => applyRecommendation(rec.param, rec.value)}
                                  style={{
                                    background: "rgba(var(--color-brand-rgb, 229, 62, 62), 0.15)",
                                    border:
                                      "1px solid rgba(var(--color-brand-rgb, 229, 62, 62), 0.3)",
                                    color: "var(--color-brand, #E53E3E)",
                                    borderRadius: "6px",
                                    padding: "4px 12px",
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    transition: "all 0.2s",
                                  }}
                                >
                                  Aplicar
                                </button>
                              )}
                              {rec.reason.includes("aplicado") && (
                                <span
                                  style={{ color: "#22c55e", fontSize: "0.7rem", fontWeight: 700 }}
                                >
                                  <i className="bx bx-check" style={{ fontSize: "1rem" }}></i>
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {assistantLoading && (
                  <div style={{ display: "flex", justifyContent: "flex-start" }}>
                    <div
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "12px 12px 12px 2px",
                        padding: "10px 14px",
                      }}
                    >
                      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                        <i
                          className="bx bx-loader-alt bx-spin"
                          style={{ fontSize: "0.9rem", color: "var(--color-brand, #E53E3E)" }}
                        ></i>
                        <span style={{ fontSize: "0.75rem", color: "#888" }}>
                          Analizando parametros...
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={assistantEndRef} />
              </div>

              <div style={{ display: "flex", gap: "8px", position: "relative" }}>
                <input
                  type="text"
                  value={assistantInput}
                  onChange={(e) => setAssistantInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendAssistantMessage();
                    }
                  }}
                  placeholder="Describe que tipo de respuesta quieres..."
                  style={{
                    flex: 1,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "#fff",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    paddingRight: "40px", // Extra space for the mic icon
                    fontSize: "0.78rem",
                    fontFamily: "inherit",
                    outline: "none",
                  }}
                />
                {/* Microphone Button inside the input ideally, or next to it */}
                <button
                  onClick={toggleListening}
                  style={{
                    position: "absolute",
                    right: "65px", // Next to the send button
                    background: "transparent",
                    border: "none",
                    color: isListening ? "#E53E3E" : "#888",
                    padding: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "color 0.2s",
                    outline: "none",
                  }}
                  title={isListening ? "Detener dictado" : "Dictar por voz"}
                >
                  <i
                    className={isListening ? "bx bxs-microphone bx-flashing" : "bx bx-microphone"}
                    style={{ fontSize: "1.2rem" }}
                  ></i>
                </button>
                <button
                  onClick={sendAssistantMessage}
                  disabled={!assistantInput.trim() || assistantLoading}
                  style={{
                    position: "relative",
                    background: "rgba(var(--color-brand-rgb, 229, 62, 62), 0.15)",
                    border: "1px solid rgba(var(--color-brand-rgb, 229, 62, 62), 0.3)",
                    color: "var(--color-brand, #E53E3E)",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    cursor: assistantLoading ? "not-allowed" : "pointer",
                    opacity: !assistantInput.trim() || assistantLoading ? 0.4 : 1,
                    transition: "all 0.2s",
                  }}
                >
                  <i className="bx bx-send" style={{ fontSize: "1rem" }}></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ TEXTAREA EDIT MODAL ═══ */}
      {editingTextarea && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "var(--color-panel-bg, #1e1e2e)",
              border: "1px solid var(--color-panel-border)",
              borderRadius: "16px",
              padding: "24px",
              width: "700px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          >
            <h3
              style={{ margin: "0 0 12px 0", fontSize: "0.95rem", fontWeight: 800, color: "#fff" }}
            >
              {editingTextarea.label}
            </h3>
            <textarea
              value={(() => {
                const v = getNestedValue(editConfig, editingTextarea.key);
                if (typeof v === "object") return JSON.stringify(v, null, 2);
                return v ?? "";
              })()}
              onChange={(e) => {
                const tooltipDef = tooltips[editingTextarea!.key];
                if (tooltipDef?.type === "json") {
                  try {
                    handleParamChange(editingTextarea!.key, JSON.parse(e.target.value));
                  } catch {
                    /* invalid JSON, just store raw */
                  }
                } else {
                  handleParamChange(editingTextarea!.key, e.target.value);
                }
              }}
              style={{
                flex: 1,
                minHeight: "300px",
                padding: "14px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid var(--color-panel-border)",
                borderRadius: "8px",
                color: "#ddd",
                fontSize: "0.8rem",
                fontFamily: "monospace",
                lineHeight: "1.5",
                outline: "none",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
              <button
                onClick={() => setEditingTextarea(null)}
                style={{
                  padding: "8px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: "var(--color-brand, #E53E3E)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  cursor: "pointer",
                }}
              >
                CERRAR
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CREATE PRESET DIALOG ═══ */}
      {showCreateDialog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "var(--color-panel-bg)",
              border: "1px solid var(--color-panel-border)",
              borderRadius: "16px",
              padding: "30px",
              width: "400px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: 800, color: "#fff" }}>
              Crear nuevo preset
            </h3>
            <p style={{ fontSize: "0.78rem", color: "#888", margin: "0 0 16px" }}>
              Se duplicará la configuración de "{selectedPreset?.name || "Default"}"
            </p>
            <input
              type="text"
              autoFocus
              value={newPresetName}
              onChange={(e) => setNewPresetName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createPreset()}
              placeholder="Nombre del preset..."
              style={{
                width: "100%",
                padding: "10px 14px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid var(--color-panel-border)",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "0.85rem",
                outline: "none",
                boxSizing: "border-box",
                marginBottom: "16px",
              }}
            />
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setNewPresetName("");
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "1px solid var(--color-panel-border)",
                  background: "transparent",
                  color: "#888",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                }}
              >
                CANCELAR
              </button>
              <button
                onClick={createPreset}
                disabled={!newPresetName.trim()}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  background: newPresetName.trim() ? "var(--color-brand, #E53E3E)" : "#333",
                  color: "#fff",
                  cursor: newPresetName.trim() ? "pointer" : "default",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                }}
              >
                CREAR
              </button>
            </div>
          </div>
        </div>
      )}
      {renderInfoTooltipOverlay()}

      {/* Help Modal */}
      {showHelpModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(8px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px",
          }}
          onClick={() => setShowHelpModal(false)}
        >
          <div
            style={{
              background: "#12121e",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.1)",
              width: "100%",
              maxWidth: "900px",
              maxHeight: "100%",
              overflowY: "auto",
              padding: "40px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowHelpModal(false)}
              style={{
                position: "absolute",
                top: "24px",
                right: "24px",
                background: "rgba(255,255,255,0.1)",
                border: "none",
                color: "#fff",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
            >
              <i className="bx bx-x" style={{ fontSize: "1.2rem" }}></i>
            </button>
            <div
              style={{
                color: "#fff",
                fontSize: "0.95rem",
                lineHeight: 1.6,
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <style>{`
                                .brain-engine-help h1, .brain-engine-help h2, .brain-engine-help h3 {
                                    color: #fff;
                                    margin-bottom: 0.5rem;
                                    margin-top: 1.5rem;
                                    border-bottom: 1px solid rgba(255,255,255,0.1);
                                    padding-bottom: 0.3rem;
                                }
                                .brain-engine-help h1 { font-size: 1.8rem; border-bottom: none; margin-top: 0; }
                                .brain-engine-help h2 { font-size: 1.3rem; }
                                .brain-engine-help h3 { font-size: 1.1rem; border-bottom: none; }
                                .brain-engine-help p { margin-bottom: 1rem; color: #ccc; }
                                .brain-engine-help ul { margin-left: 1.5rem; margin-bottom: 1rem; color: #ccc; }
                                .brain-engine-help li { margin-bottom: 0.3rem; }
                                .brain-engine-help blockquote { border-left: 4px solid #06b6d4; padding-left: 1rem; margin-left: 0; color: #aaa; background: rgba(6, 182, 212, 0.1); padding: 0.5rem 1rem; border-radius: 0 4px 4px 0; }
                                .brain-engine-help table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; font-size: 0.85rem; }
                                .brain-engine-help th, .brain-engine-help td { border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; text-align: left; }
                                .brain-engine-help th { background: rgba(255,255,255,0.05); color: #fff; font-weight: 600; }
                                .brain-engine-help hr { border: none; border-top: 1px solid rgba(255,255,255,0.1); margin: 2rem 0; }
                            `}</style>
              <div className="brain-engine-help">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{helpMarkdownText}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      <CustomDialog
        isOpen={!!globalAlert?.isOpen}
        type={globalAlert?.type || "alert"}
        title={globalAlert?.title || t("admin.brainEngine.attention")}
        message={globalAlert?.message || ""}
        onConfirm={() => setGlobalAlert(null)}
        onCancel={() => setGlobalAlert(null)}
      />
    </div>
  );
};
